import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CloudRain, MessageSquareHeart, Megaphone, X } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";

const fmt = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const popRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const unread = items.filter((i) => !i.read).length;

  const readAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems(items.map((i) => ({ ...i, read: true })));
    } catch (e) { toast.error(formatErr(e)); }
  };

  const readOne = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems(items.map((i) => i.notification_id === id ? { ...i, read: true } : i));
    } catch {}
  };

  return (
    <div className="relative" ref={popRef}>
      <button onClick={() => setOpen(!open)} className="relative p-1.5" data-testid="bell-btn" aria-label="Notifications">
        <Bell className="w-5 h-5" strokeWidth={1.5} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-[#ff4500] text-white text-[10px] mono font-bold px-1.5 py-0.5 leading-none"
            data-testid="bell-unread">{unread}</span>
        )}
      </button>

      {open && (
        <div className="fixed sm:absolute right-2 sm:right-0 left-2 sm:left-auto top-14 sm:top-10 sm:w-[380px] max-h-[70vh] flex flex-col bg-white border hairline-strong shadow-lg z-[60]" data-testid="bell-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b hairline">
            <div className="eyebrow">Weather Alerts</div>
            <div className="flex items-center gap-3">
              {unread > 0 && <button onClick={readAll} className="text-xs underline" data-testid="read-all">Mark all read</button>}
              <button onClick={() => setOpen(false)}><X className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-sm text-[#888] text-center">
                No alerts. Add a trip date to a favorite to get a 48h weather heads-up.
              </div>
            ) : items.map((n, i) => {
              const Icon = n.kind === "review_prompt" ? MessageSquareHeart
                : n.kind === "new_campsites" ? Megaphone
                : CloudRain;
              const label = n.kind === "review_prompt" ? "Share a review"
                : n.kind === "new_campsites" ? "Fresh sites added"
                : `Trip: ${n.trip_start?.slice(0, 10)}`;
              const href = n.kind === "new_campsites" ? "/browse" : `/campsites/${n.campsite_id}`;
              return (
                <Link key={n.notification_id}
                  to={href}
                  onClick={() => readOne(n.notification_id)}
                  className={`flex gap-3 p-4 border-b hairline hover:bg-[#fafafa] ${n.read ? "opacity-60" : ""}`}
                  data-testid={`notif-${i}`}>
                  <Icon className="w-5 h-5 text-[#ff4500] shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <div className="font-display text-base truncate">{n.campsite_name}</div>
                    <div className="text-xs text-[#555]">{label}</div>
                    <div className="text-sm mt-1">{n.summary}</div>
                    <div className="mono text-[10px] text-[#888] mt-1">{fmt(n.created_at)}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
